<script setup lang="ts">
import type { UpcomingEvent } from '~~/server/api/upcoming-events.get'

const props = withDefaults(defineProps<{
  limit?: number
  category?: string
}>(), {
  limit: 10,
  category: undefined
})

// Läuft serverseitig, damit die Termine im ausgelieferten HTML stehen statt erst
// client-seitig nachgeladen zu werden (siehe useLiveCourses.ts / routeRules in
// nuxt.config.ts für die zugehörige SWR-Cachezeit statt statischem Prerendering).
const { data, status } = useAsyncData(
  () => `upcoming-events-${props.limit}`,
  () => $fetch<{ events: UpcomingEvent[] }>('/api/upcoming-events', { query: { limit: props.limit } }),
  {
    default: () => ({ events: [] })
  }
)

const events = computed(() => {
  const all = data.value?.events ?? []
  if (!props.category) return all
  return all.filter(e => e.category?.toLowerCase() === props.category!.toLowerCase())
})
const isLoading = computed(() => status.value === 'pending' || status.value === 'idle')

// Link-Ziel aus dem Kurstyp-Slug gegen die Content-Collection auflösen.
// Die vom Backend gelieferte `event.url` verwendet teils abweichende
// Kategorie-Slugs und ist daher nicht zuverlässig.
const { courseUrl } = useCourseUrl()

function eventUrl(event: UpcomingEvent): string | null {
  return courseUrl(event.slug) ?? event.url
}

function formatDate(value: string): string {
  if (!value) return ''
  return new Date(value).toLocaleDateString('de-CH', {
    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric'
  })
}

function isoDate(value: string): string | undefined {
  if (!value) return undefined
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString().slice(0, 10)
}
</script>

<template>
  <div>
    <div
      v-if="isLoading"
      class="flex justify-center py-8"
    >
      <UIcon
        name="i-lucide-loader-circle"
        class="animate-spin text-2xl text-primary"
      />
    </div>

    <UAlert
      v-else-if="!events.length"
      icon="i-lucide-info"
      color="neutral"
      variant="subtle"
      title="Keine kommenden Termine"
      description="Aktuell sind keine zukünftigen Kurse oder Veranstaltungen ausgeschrieben."
    />

    <div
      v-else
      class="overflow-hidden rounded-lg ring ring-default"
    >
      <table class="w-full text-base">
        <caption class="sr-only">
          Kommende Kurse und Veranstaltungen
        </caption>
        <thead class="bg-elevated text-default">
          <tr>
            <th
              scope="col"
              class="text-left font-semibold px-4 py-3 hidden sm:table-cell"
            >
              Beginn
            </th>
            <th
              scope="col"
              class="text-left font-semibold px-4 py-3"
            >
              Kurs / Veranstaltung
            </th>
            <th
              scope="col"
              class="text-left font-semibold px-4 py-3 hidden md:table-cell"
            >
              Kategorie
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="event in events"
            :key="event.id"
            class="border-t border-default hover:bg-elevated/50 transition-colors"
          >
            <td class="align-top px-4 py-3 whitespace-nowrap text-muted hidden sm:table-cell">
              <time :datetime="isoDate(event.date)">{{ formatDate(event.date) }}</time>
            </td>
            <td class="align-top px-4 py-3">
              <div class="text-sm text-muted sm:hidden mb-0.5">
                <time :datetime="isoDate(event.date)">{{ formatDate(event.date) }}</time>
              </div>
              <NuxtLink
                v-if="eventUrl(event)"
                :to="eventUrl(event)!"
                class="text-primary hover:underline font-medium"
              >
                {{ event.title }}
              </NuxtLink>
              <span
                v-else
                class="font-medium"
              >{{ event.title }}</span>
              <div
                v-if="event.category"
                class="md:hidden mt-1"
              >
                <UBadge
                  color="neutral"
                  variant="subtle"
                  size="sm"
                  class="text-xs"
                >
                  {{ event.category }}
                </UBadge>
              </div>
            </td>
            <td class="align-top px-4 py-3 hidden md:table-cell">
              <UBadge
                v-if="event.category"
                color="neutral"
                variant="subtle"
                size="sm"
                class="text-xs"
              >
                {{ event.category }}
              </UBadge>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
